import { useEffect, useRef, useState } from "react"
import { useMap } from "@vis.gl/react-google-maps"

import { colors } from "@/theme"

interface Location {
  latitude: number
  longitude: number
}

interface AnimatedRouteLineProps {
  origin: Location | null
  destination: Location | null
  showAnimation: boolean
  isReturn?: boolean // When true, animate from destination to origin
  /**
   * PLA-272 stale-GPS affordance: while true the shimmer marker is hidden and
   * its rAF loop stops (the static polyline stays visible); unpausing rebuilds
   * the shimmer from the start of the path.
   */
  paused?: boolean
}

/**
 * Calculate control point for quadratic Bézier curve
 */
const getControlPoint = (
  start: google.maps.LatLngLiteral,
  end: google.maps.LatLngLiteral,
): google.maps.LatLngLiteral => {
  const midLat = (start.lat + end.lat) / 2
  const midLng = (start.lng + end.lng) / 2

  // Calculate perpendicular offset for curve
  const dx = end.lng - start.lng
  const dy = end.lat - start.lat
  const distance = Math.sqrt(dx * dx + dy * dy)
  const offset = distance * 0.15 // 15% offset for subtle curve

  // Perpendicular vector
  const perpX = -dy / distance
  const perpY = dx / distance

  return {
    lat: midLat + perpX * offset,
    lng: midLng + perpY * offset,
  }
}

/**
 * Generate points along a quadratic Bézier curve
 */
const generateBezierPoints = (
  start: google.maps.LatLngLiteral,
  control: google.maps.LatLngLiteral,
  end: google.maps.LatLngLiteral,
  numPoints: number = 100,
): google.maps.LatLngLiteral[] => {
  const points: google.maps.LatLngLiteral[] = []

  for (let i = 0; i <= numPoints; i++) {
    const t = i / numPoints
    const oneMinusT = 1 - t

    // Quadratic Bézier formula: B(t) = (1-t)²P₀ + 2(1-t)tP₁ + t²P₂
    const lat =
      oneMinusT * oneMinusT * start.lat + 2 * oneMinusT * t * control.lat + t * t * end.lat

    const lng =
      oneMinusT * oneMinusT * start.lng + 2 * oneMinusT * t * control.lng + t * t * end.lng

    points.push({ lat, lng })
  }

  return points
}

export const AnimatedRouteLine = ({
  origin,
  destination,
  showAnimation,
  isReturn = false,
  paused = false,
}: AnimatedRouteLineProps) => {
  const map = useMap()
  const polylineRef = useRef<google.maps.Polyline | null>(null)
  const shimmerMarkerRef = useRef<google.maps.marker.AdvancedMarkerElement | null>(null)
  const animationRef = useRef<number | null>(null)
  const [_shimmerPosition, setShimmerPosition] = useState(0)

  // Rendering animated route line

  // Reset shimmer position when isReturn changes
  useEffect(() => {
    // Route direction changed
    setShimmerPosition(0)
  }, [isReturn])

  useEffect(() => {
    if (!map || !origin || !destination) {
      // Clean up if conditions not met
      if (polylineRef.current) {
        polylineRef.current.setMap(null)
        polylineRef.current = null
      }
      if (shimmerMarkerRef.current) {
        shimmerMarkerRef.current.map = null
        shimmerMarkerRef.current = null
      }
      return
    }

    // Convert locations to LatLng
    // When isReturn is true, swap start and end to animate from customer back to hub
    const start = isReturn
      ? { lat: destination.latitude, lng: destination.longitude }
      : { lat: origin.latitude, lng: origin.longitude }
    const end = isReturn
      ? { lat: origin.latitude, lng: origin.longitude }
      : { lat: destination.latitude, lng: destination.longitude }

    // Route direction and animation points configured

    // Calculate control point for curve
    const control = getControlPoint(start, end)

    // Generate Bézier curve points
    const curvePoints = generateBezierPoints(start, control, end)

    // Create or update polyline
    if (!polylineRef.current) {
      polylineRef.current = new google.maps.Polyline({
        path: curvePoints,
        geodesic: false,
        strokeColor: colors.primary, // #FF382F
        strokeOpacity: 0.8,
        strokeWeight: 4,
        map: map,
        zIndex: 1, // Below markers
      })
    } else {
      polylineRef.current.setPath(curvePoints)
    }

    // Handle shimmer animation. PLA-272: a paused line (stale GPS) takes the
    // else-branch — the rAF loop stops and the shimmer marker is removed while
    // the static polyline above stays; unpausing re-runs this effect and
    // rebuilds the shimmer from the start of the path.
    if (showAnimation && !paused && google.maps.marker?.AdvancedMarkerElement) {
      // Clean up existing shimmer marker when path changes
      if (shimmerMarkerRef.current) {
        shimmerMarkerRef.current.map = null
        shimmerMarkerRef.current = null
      }

      // Create new shimmer marker
      if (true) {
        const shimmerContent = document.createElement("div")
        shimmerContent.style.width = "8px"
        shimmerContent.style.height = "8px"
        shimmerContent.style.borderRadius = "50%"
        shimmerContent.style.backgroundColor = colors.surface
        shimmerContent.style.boxShadow = "0 0 12px 6px rgba(255, 255, 255, 0.6)"
        shimmerContent.style.position = "absolute"
        shimmerContent.style.transform = "translate(-50%, -50%)"
        shimmerContent.style.transformOrigin = "center center"
        shimmerContent.style.animation = "pulse 1s ease-in-out infinite"

        // Add CSS animation
        if (!document.getElementById("shimmer-animation-styles")) {
          const style = document.createElement("style")
          style.id = "shimmer-animation-styles"
          style.textContent = `
            @keyframes pulse {
              0% {
                transform: translate(-50%, -50%) scale(1);
                opacity: 0.8;
              }
              50% {
                transform: translate(-50%, -50%) scale(1.2);
                opacity: 1;
              }
              100% {
                transform: translate(-50%, -50%) scale(1);
                opacity: 0.8;
              }
            }
          `
          document.head.appendChild(style)
        }

        shimmerMarkerRef.current = new google.maps.marker.AdvancedMarkerElement({
          map: map,
          position: curvePoints[0],
          content: shimmerContent,
          zIndex: 2, // Above line, below other markers
        })
      }

      // Animate shimmer along the path
      let animationProgress = 0 // Always start from the beginning when path changes

      const animate = () => {
        animationProgress += 0.005 // Move 0.5% per frame (approx 60fps = 3.33s for full path)

        if (animationProgress >= 1) {
          animationProgress = 0 // Reset to start
        }

        const pointIndex = Math.floor(animationProgress * (curvePoints.length - 1))
        const nextIndex = Math.min(pointIndex + 1, curvePoints.length - 1)
        const localProgress = (animationProgress * (curvePoints.length - 1)) % 1

        // Interpolate between points for smooth movement
        const currentPoint = curvePoints[pointIndex]
        const nextPoint = curvePoints[nextIndex]

        const interpolatedPosition = {
          lat: currentPoint.lat + (nextPoint.lat - currentPoint.lat) * localProgress,
          lng: currentPoint.lng + (nextPoint.lng - currentPoint.lng) * localProgress,
        }

        if (shimmerMarkerRef.current) {
          shimmerMarkerRef.current.position = interpolatedPosition
        }

        setShimmerPosition(animationProgress)
        animationRef.current = requestAnimationFrame(animate)
      }

      // Start animation
      animationRef.current = requestAnimationFrame(animate)
    } else {
      // Stop animation and hide shimmer
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
        animationRef.current = null
      }
      if (shimmerMarkerRef.current) {
        shimmerMarkerRef.current.map = null
        shimmerMarkerRef.current = null
      }
    }

    // Cleanup
    return () => {
      if (animationRef.current) {
        cancelAnimationFrame(animationRef.current)
      }
      if (polylineRef.current) {
        polylineRef.current.setMap(null)
        polylineRef.current = null
      }
      if (shimmerMarkerRef.current) {
        shimmerMarkerRef.current.map = null
        shimmerMarkerRef.current = null
      }
    }
  }, [map, origin, destination, showAnimation, isReturn, paused])

  return null // This component doesn't render anything directly
}
